import {
  Button,
  Card,
  Col,
  Input,
  message,
  Modal,
  Row,
  Spin,
  Tag,
  Typography,
} from 'antd';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import { fetchAPI } from '../../../utils/api';

const { TextArea } = Input;
const { Text, Title } = Typography;

interface AmazonOrderData {
  po_number?: string;
  order_info?: any;
  order_items?: any;
  shipping_info?: any;
  last_sync_at?: string;
  sync_identifiers?: any;
}

interface SyncResult {
  success: boolean;
  order_id: number;
  amazon_po_number: string;
  record?: AmazonOrderData;
  error?: string;
}

const ModalAmazonOrderSync = (props: {
  visible: boolean;
  onCancel: () => void;
  orderId: number;
  onSuccess?: () => void; // Optional: callback when sync completes successfully
}) => {
  const { visible, orderId, onSuccess } = props;
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SyncResult | null>(null);

  // Auto-sync data when modal opens
  useEffect(() => {
    const loadData = async () => {
      if (orderId && visible) {
        setLoading(true);
        setResult(null);
        try {
          const response = await fetchAPI('order/amazon/sync', {
            method: 'POST',
            payload: { order_id: orderId },
          });
          setResult(response);
          // message.success('Amazon order synced successfully');
          if (onSuccess) {
            onSuccess();
          }
        } catch (error) {
          const errorMsg =
            error?.message ||
            error?.error_message ||
            'Failed to sync Amazon order';
          message.error(errorMsg);
        } finally {
          setLoading(false);
        }
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, visible]);

  const syncOrder = async () => {
    setLoading(true);
    setResult(null);
    try {
      const response = await fetchAPI('order/amazon/sync', {
        method: 'POST',
        payload: {
          order_id: orderId,
        },
      });
      setResult(response);
      message.success('Amazon order synced successfully');
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const errorMsg =
        error?.message || error?.error_message || 'Failed to sync Amazon order';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setResult(null);
    props.onCancel();
  };

  // Format order info for display
  const formatOrderInfo = (orderInfo: any) => {
    if (!orderInfo || !orderInfo.ordersReport) return null;

    const orders = orderInfo.ordersReport;
    return orders.map((order: any, index: number) => {
      const {
        orderMetadata,
        purchaseOrderNumber,
        orderStatus,
        buyingCustomer,
        buyerGroup,
        charges,
      } = order;

      const subtotal =
        charges?.find((c: any) => c.type === 'SUBTOTAL')?.amount?.amount || 0;
      const tax =
        charges?.find((c: any) => c.type === 'TAX')?.amount?.amount || 0;
      const shipping =
        charges?.find((c: any) => c.type === 'SHIPPING_AND_HANDLING')?.amount
          ?.amount || 0;
      const total =
        charges?.find((c: any) => c.type === 'NET_TOTAL')?.amount?.amount || 0;
      const currency =
        charges?.find((c: any) => c.amount)?.amount?.currencyCode || 'JPY';

      return (
        <Card
          key={index}
          size="small"
          style={{ marginBottom: 12 }}
          title={
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>Order #{index + 1}</span>
              <Tag color={orderStatus === 'CLOSED' ? 'green' : 'blue'}>
                {orderStatus}
              </Tag>
            </div>
          }
        >
          <Row gutter={16}>
            <Col span={12}>
              <Text type="secondary">Amazon Order ID:</Text>
              <br />
              <Text strong>{orderMetadata?.orderId}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">PO Number:</Text>
              <br />
              <Text strong>{purchaseOrderNumber}</Text>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Text type="secondary">Order Date:</Text>
              <br />
              <Text>
                {orderMetadata?.orderDate
                  ? new Date(orderMetadata.orderDate).toLocaleString()
                  : '-'}
              </Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">Region:</Text>
              <br />
              <Text>{orderMetadata?.region}</Text>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Text type="secondary">Customer:</Text>
              <br />
              <Text>{buyingCustomer?.name}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">Email:</Text>
              <br />
              <Text copyable>{buyingCustomer?.email}</Text>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={24}>
              <Text type="secondary">Buyer Group:</Text>
              <br />
              <Text>{buyerGroup?.name}</Text>
            </Col>
          </Row>
          <Divider />
          <Row gutter={16}>
            <Col span={6}>
              <Text type="secondary">Subtotal:</Text>
              <br />
              <Text strong>
                {currency} {subtotal.toLocaleString()}
              </Text>
            </Col>
            <Col span={6}>
              <Text type="secondary">Tax:</Text>
              <br />
              <Text strong>
                {currency} {tax.toLocaleString()}
              </Text>
            </Col>
            <Col span={6}>
              <Text type="secondary">Shipping:</Text>
              <br />
              <Text strong>
                {currency} {shipping.toLocaleString()}
              </Text>
            </Col>
            <Col span={6}>
              <Text type="secondary">Total:</Text>
              <br />
              <Text strong type="success">
                {currency} {total.toLocaleString()}
              </Text>
            </Col>
          </Row>
        </Card>
      );
    });
  };

  // Format shipping info for display
  const formatShippingInfo = (shippingInfo: any) => {
    if (!shippingInfo || !shippingInfo.shipmentsReport) return null;

    const shipments = shippingInfo.shipmentsReport;
    return shipments.map((shipment: any, index: number) => {
      const {
        orderMetadata,
        shipmentMetadata,
        purchaseOrderNumber,
        shipmentStatus,
        shippingAddress,
        charges,
        deliveryInfo,
      } = shipment;

      const subtotal =
        charges?.find((c: any) => c.type === 'SUBTOTAL')?.amount?.amount || 0;
      const tax =
        charges?.find((c: any) => c.type === 'TAX')?.amount?.amount || 0;
      const shipping =
        charges?.find((c: any) => c.type === 'SHIPPING_AND_HANDLING')?.amount
          ?.amount || 0;
      const total =
        charges?.find((c: any) => c.type === 'NET_TOTAL')?.amount?.amount || 0;
      const currency =
        charges?.find((c: any) => c.amount)?.amount?.currencyCode || 'JPY';

      const fullAddress = [
        shippingAddress?.addressLine1,
        shippingAddress?.addressLine2,
        shippingAddress?.city,
        shippingAddress?.stateOrRegion,
        shippingAddress?.postalCode,
        shippingAddress?.countryCode,
      ]
        .filter(Boolean)
        .join(', ');

      return (
        <Card
          key={index}
          size="small"
          style={{ marginBottom: 12 }}
          title={
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>Shipment #{index + 1}</span>
              <Tag color={shipmentStatus === 'SHIPPED' ? 'green' : 'blue'}>
                {shipmentStatus}
              </Tag>
            </div>
          }
        >
          <Row gutter={16}>
            <Col span={12}>
              <Text type="secondary">Amazon Order ID:</Text>
              <br />
              <Text strong>{orderMetadata?.orderId}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">PO Number:</Text>
              <br />
              <Text strong>{purchaseOrderNumber}</Text>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Text type="secondary">Shipment ID:</Text>
              <br />
              <Text>{shipmentMetadata?.shipmentId}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">Shipment Date:</Text>
              <br />
              <Text>
                {shipmentMetadata?.shipmentDate
                  ? new Date(shipmentMetadata.shipmentDate).toLocaleString()
                  : '-'}
              </Text>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Text type="secondary">Recipient:</Text>
              <br />
              <Text>{shippingAddress?.fullName}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">Region:</Text>
              <br />
              <Text>{orderMetadata?.region}</Text>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={24}>
              <Text type="secondary">Shipping Address:</Text>
              <br />
              <Text>{fullAddress}</Text>
            </Col>
          </Row>
          {deliveryInfo && (
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={12}>
                <Text type="secondary">Expected Delivery:</Text>
                <br />
                <Text>
                  {deliveryInfo.expectedDeliveryDate
                    ? new Date(
                        deliveryInfo.expectedDeliveryDate
                      ).toLocaleString()
                    : '-'}
                </Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Delivery Status:</Text>
                <br />
                <Tag
                  color={
                    deliveryInfo.status === 'DELIVERED'
                      ? 'green'
                      : deliveryInfo.status === 'OUT_FOR_DELIVERY'
                      ? 'orange'
                      : 'blue'
                  }
                >
                  {deliveryInfo.status}
                </Tag>
              </Col>
            </Row>
          )}
          <Divider />
          <Row gutter={16}>
            <Col span={6}>
              <Text type="secondary">Subtotal:</Text>
              <br />
              <Text strong>
                {currency} {subtotal.toLocaleString()}
              </Text>
            </Col>
            <Col span={6}>
              <Text type="secondary">Tax:</Text>
              <br />
              <Text strong>
                {currency} {tax.toLocaleString()}
              </Text>
            </Col>
            <Col span={6}>
              <Text type="secondary">Shipping:</Text>
              <br />
              <Text strong>
                {currency} {shipping.toLocaleString()}
              </Text>
            </Col>
            <Col span={6}>
              <Text type="secondary">Total:</Text>
              <br />
              <Text strong type="success">
                {currency} {total.toLocaleString()}
              </Text>
            </Col>
          </Row>
        </Card>
      );
    });
  };

  return (
    <Modal
      title={`Amazon Order Sync — Order #${orderId}`}
      visible={props.visible}
      onCancel={handleCancel}
      width={800}
      footer={null}
    >
      <Wrapper>
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <Button type="primary" onClick={syncOrder} loading={loading}>
            Refresh
          </Button>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Spin />
          </div>
        )}

        {/* Success Result with Data */}
        {result && !loading && result.success && result.record?.order_info && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Amazon Order Details</Title>
            {formatOrderInfo(result.record.order_info)}

            {result.record?.shipping_info && (
              <>
                <Title level={5} style={{ marginTop: 16 }}>
                  Shipping Information
                </Title>
                {formatShippingInfo(result.record.shipping_info)}
              </>
            )}
          </div>
        )}

        {/* No Data Found */}
        {result && !loading && result.success && !result.record?.order_info && (
          <AlertBox
            style={{
              background: '#fff7e6',
              border: '#faad14',
              marginTop: 16,
            }}
          >
            <Text type="warning" strong>
              Amazon PO data not found for PO {orderId}
            </Text>
          </AlertBox>
        )}

        {/* Raw JSON for debugging */}
        {result && !loading && (
          <div style={{ marginTop: 16 }}>
            <details>
              <summary style={{ cursor: 'pointer', color: '#1890ff' }}>
                Show Raw JSON Data
              </summary>
              <TextArea
                value={JSON.stringify(result?.record, null, 2)}
                readOnly
                rows={10}
                style={{ marginTop: 8, fontFamily: 'monospace' }}
              />
            </details>
          </div>
        )}
      </Wrapper>
    </Modal>
  );
};

const Wrapper = styled.div``;

const AlertBox = styled.div`
  padding: 12px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
`;

const Divider = styled.hr`
  margin: 12px 0;
  border: 0;
  border-top: 1px solid #f0f0f0;
`;

export default ModalAmazonOrderSync;
